/**
 * 推薦點（窗屏 4／5 階畫面），掛在景點底下（`admin.spot` 權限）。
 *
 * 與景點的文字欄位不同，推薦點是**即時儲存**（獨立 API），不隨景點的「儲存
 * 變更」一起送出——一個景點有 20 筆卡片，等表單送出會讓「我剛剛改的存了沒」
 * 完全無從判斷。
 *
 * 與 `./spots` 分檔：那支已含景點、插槽、資料夾套用三組端點；推薦點自己就有
 * 四個端點加一組型別。
 */

import { apiFetch, type Schemas } from './core';

export type SpotPoi = Schemas['SpotPoiResponseDto'];

/**
 * 分類代碼即船上的資料夾名（`4_food/`、`5_ent/`…）。
 *
 * 是 `ent` 不是 `play`——規格書文字與 ITRI 交付的 13 組樣本不一致，以樣本為準。
 */
export const POI_CATEGORIES = ['food', 'drinks', 'ent', 'shopping'] as const;
export type PoiCategory = (typeof POI_CATEGORIES)[number];

export const POI_CATEGORY_LABEL: Record<PoiCategory, string> = {
  food: '美食',
  drinks: '飲品',
  ent: '娛樂',
  shopping: '購物',
};

/** 每分類上限，與資料庫的 `seq` CHECK 一致。 */
export const POI_MAX_SEQ = 5;

/**
 * 保留手寫（同 SpotMediaList 的理由）：generated 版的 `categories` 是
 * `Record<string, …>`——OpenAPI 的 additionalProperties 表達不了「鍵必為分類
 * 列舉」，用它會讓 `categories[category]` 失去鍵的型別檢查。
 */
export interface SpotPoiList {
  spot_id: string;
  categories: Record<PoiCategory, SpotPoi[]>;
  counts: Record<PoiCategory, number>;
}

/**
 * 可寫欄位。
 *
 * **未出現＝不變更，出現且為 null＝清空**——兩者語意不同，所以清空不能用省略
 * 表達。這與後端的 upsert 語意一致。
 */
export interface SpotPoiPatch {
  store_id?: string;
  name_i18n?: Record<string, string>;
  keywords_i18n?: Record<string, string[]>;
  nearby_i18n?: Record<string, string>;
  intro_i18n?: Record<string, string>;
  hours_i18n?: Record<string, string>;
  asr_hint_i18n?: Record<string, string>;
  rating?: number | null;
  thumb_media_id?: string | null;
  photo_media_id?: string | null;
  qr_media_id?: string | null;
  map_url?: string | null;
  /**
   * 機翻記號的**增減**（不是整包取代）：`{"intro_i18n":{"en":"mt_draft"}}`，
   * 值傳 null 表示「人已校對，拿掉記號」。鍵是資料表欄位名，不帶 `poi.` 前綴。
   */
  translation_state?: Record<string, Record<string, 'mt_draft' | null>>;
}

const base = (spotId: string): string => `/spots/${encodeURIComponent(spotId)}/pois`;

export function listSpotPois(spotId: string): Promise<SpotPoiList> {
  return apiFetch<SpotPoiList>(base(spotId), { method: 'GET' });
}

export function upsertSpotPoi(
  spotId: string,
  category: PoiCategory,
  seq: number,
  patch: SpotPoiPatch,
): Promise<SpotPoi> {
  return apiFetch<SpotPoi>(`${base(spotId)}/${category}/${seq}`, { method: 'PUT', body: patch });
}

export function deleteSpotPoi(
  spotId: string,
  category: PoiCategory,
  seq: number,
): Promise<SpotPoiList> {
  return apiFetch<SpotPoiList>(`${base(spotId)}/${category}/${seq}`, { method: 'DELETE' });
}

/** 傳入該分類**全部**推薦點的 id，順序即新的 seq。 */
export function reorderSpotPois(
  spotId: string,
  category: PoiCategory,
  order: string[],
): Promise<SpotPoiList> {
  return apiFetch<SpotPoiList>(`${base(spotId)}/${category}/order`, {
    method: 'PUT',
    body: { order },
  });
}

/**
 * 由店名推一個語音提示。
 *
 * 樣本的寫法是取店名的特徵詞：「阿美深海鮮魚湯」→ `[說"阿美"]`、
 * 「福爾摩沙遊艇酒店－威尼斯餐廳」→ `[說"威尼斯"]`。機器猜不準（前者取前兩字、
 * 後者取破折號後的專名），所以這只是**建議值**，填進欄位後可自由改。
 *
 * 規則：有分隔符就取最後一段，再去掉常見的業態後綴，最後截到 4 個字以內。
 */
export function suggestAsrHint(name: string, locale: string): string {
  const trimmed = name.trim();
  if (trimmed === '') return '';

  const last =
    trimmed
      .split(/[－–—\-·・|/]/)
      .pop()
      ?.trim() ?? trimmed;
  const core = last.replace(/(餐廳|餐酒館|酒店|飯店|咖啡廳|茶館|商店|專賣店|旗艦店)$/u, '');
  const word = (core === '' ? last : core).slice(0, 4);

  return locale === 'en' ? `[say "${word}"]` : `[說"${word}"]`;
}
